// Schreiben-Assistent Etappe 4: Wiedervorlage nach dem Senden – ein Ticket je Schreiben (mit Vorgangsart der Vorlage
// ein Vorgang). Datum: Tag nach einer Frist aus den Eingabefeldern, sonst heute + Tage der Vorlage.
import { supabase } from '../supabase/client';
import { erstelleTicket } from '../tickets/erstelleTicket';
import type { TicketVorlage } from '../ki/ticketVorlage';
import { fuelle } from './platzhalter';
import type { EmpfaengerArt, SchreibenEmpfaenger } from './typen';

const ART: Record<EmpfaengerArt, string> = { mieter: 'Mieter', eigentuemer: 'Eigentümer', kontakt: 'Kontakt', dienstleister: 'Dienstleister' };

const plusTage = (iso: string, tage: number) => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + tage);
  return d.toISOString().slice(0, 10);
};

/** heute: „JJJJ-MM-TT“; eingaben im Format des Assistenten (Datum „TT.MM.JJJJ“). null = keine Vorbelegung. */
export function wiedervorlageDatum(heute: string, tage: number | null, eingaben: Record<string, string>): string | null {
  for (const [k, v] of Object.entries(eingaben)) {
    const m = /frist/i.test(k) ? /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(v.trim()) : null;
    if (!m) continue;
    // echtes Kalenderdatum? (31.02. oder 01.13. übergehen statt weiterzurollen bzw. abzustürzen)
    const iso = `${m[3]}-${m[2]}-${m[1]}`, d = new Date(`${iso}T12:00:00Z`);
    if (!Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === iso) return plusTage(iso, 1);
  }
  return tage ? plusTage(heute, tage) : null;
}

export interface WiedervorlageWahl { an: boolean; datum: string; notiz: string; manuell: boolean }

/** Vorbelegung (neu, solange man nichts selbst eingestellt hat – z. B. Frist erst später eingetragen). */
export function vorbelegeWiedervorlage(alt: WiedervorlageWahl | null, heute: string, tage: number | null, eingaben: Record<string, string>): WiedervorlageWahl {
  if (alt?.manuell) return alt;
  const datum = wiedervorlageDatum(heute, tage, eingaben);
  return { an: !!datum, datum: datum ?? plusTage(heute, 14), notiz: alt?.notiz ?? '', manuell: false };
}

/** Ticket-Titel: Eingaben (für alle gleich) gefüllt; Empfängerwerte nur bei einem Empfänger, sonst Platzhalter weg. */
export function ticketBetreff(betreff: string, eingaben: Record<string, string>, werte: Record<string, string> | null): string {
  return fuelle(betreff, werte ?? {}, eingaben).replace(/\{\{[^}]*\}\}/g, '').replace(/\s+/g, ' ').trim();
}

/** Betreff für Listen: bei genau einem Empfänger gefüllt, sonst Platzhalter weg; hängende Striche/Kommas am Ende weg. */
export function anzeigeBetreff(betreff: string, eingaben: Record<string, string>, werteJeEmpfaenger: Record<string, string>[]): string {
  const t = ticketBetreff(betreff, eingaben, werteJeEmpfaenger.length === 1 ? werteJeEmpfaenger[0] : null).replace(/[\s–\-,:;]+$/, '').trim();
  return t || 'Schreiben';
}

type Empf = Pick<SchreibenEmpfaenger, 'art' | 'id' | 'name' | 'objektId' | 'einheitId'>;

export function wiedervorlageTicket(a: { betreff: string; empfaenger: Empf[]; datum: string; notiz: string; vorgangsartId: string | null; heute: string }): TicketVorlage {
  const eins = a.empfaenger.length === 1 ? a.empfaenger[0] : null;
  const objekte = [...new Set(a.empfaenger.map((e) => e.objektId))];
  const zeilen = a.empfaenger.slice(0, 30).map((e) => `- ${e.name} (${ART[e.art]})`);
  if (a.empfaenger.length > 30) zeilen.push(`… und ${a.empfaenger.length - 30} weitere`);
  return {
    titel: `Wiedervorlage: ${a.betreff || 'Schreiben'}`,
    beschreibung: [a.notiz.trim(), `Schreiben vom ${a.heute} an:\n${zeilen.join('\n')}`].filter(Boolean).join('\n\n'),
    prioritaet: 'mittel', faellig_am: a.datum, wiedervorlage_am: a.datum,
    objekt_id: objekte.length === 1 && objekte[0] ? objekte[0] : undefined,
    einheit_id: eins?.einheitId ?? null,
    mieter_id: eins?.art === 'mieter' ? eins.id : undefined,
    vorgangsart_id: a.vorgangsartId,
  };
}

/** Ticket anlegen und am Lauf vermerken. Liefert die Ticketnummer. */
export async function legeWiedervorlageAn(schreibenId: string, vorlage: TicketVorlage): Promise<{ id: string; ticket_nummer: string }> {
  const { data: { user } } = await supabase.auth.getUser();
  const t = await erstelleTicket(vorlage, user?.id ?? null);
  const { error } = await supabase.from('schreiben').update({ wiedervorlage_ticket_id: t.id }).eq('id', schreibenId);
  if (error) throw new Error(`Wiedervorlage ${t.ticket_nummer} angelegt, aber nicht im Protokoll vermerkt: ${error.message}`);
  return t;
}

/** Gespeicherter Wunsch (schreiben.optionen.wiedervorlage) – auch fürs Fortsetzen. */
export interface WiedervorlageWunsch { datum: string; notiz: string; vorgangsartId: string | null }

/** Nach einem Versand mit Erfolg: das eine Ticket des Schreibens anlegen. */
export function wiedervorlageNachVersand(a: { schreibenId: string; betreff: string; eingaben: Record<string, string>; empfaenger: SchreibenEmpfaenger[]; wunsch: WiedervorlageWunsch }) {
  const eins = a.empfaenger.length === 1 ? a.empfaenger[0] : null;
  return legeWiedervorlageAn(a.schreibenId, wiedervorlageTicket({
    betreff: ticketBetreff(a.betreff, a.eingaben, eins?.werte ?? null), empfaenger: a.empfaenger, datum: a.wunsch.datum, notiz: a.wunsch.notiz,
    vorgangsartId: a.wunsch.vorgangsartId, heute: new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' }),
  }));
}
